import { useState } from 'react';
import { Star, Trash2, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import { useLikes } from '../hooks/useLikes';

// Gender icon components
const GenderIcon = ({ gender }: { gender: 'male' | 'female' | 'neutral' }) => {
  const symbols = {
    male: { symbol: '♂', className: 'gender-male' },
    female: { symbol: '♀', className: 'gender-female' },
    neutral: { symbol: '⚥', className: 'gender-neutral' },
  };
  const config = symbols[gender];
  return <span className={cn('text-lg', config.className)}>{config.symbol}</span>;
};

export function MyLikesPage() {
  const { t } = useTranslation('names');
  const {
    likes,
    isLoading,
    filter,
    setFilter,
    stats,
    removeLike,
    toggleFavorite,
  } = useLikes();

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleRemove = async (swipeId: string) => {
    setRemovingId(swipeId);
    await removeLike(swipeId);
    setRemovingId(null);
    setConfirmDeleteId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-2">{t('likesPage.title')}</h1>
      <p className="text-white/70 text-sm mb-4">
        {t('likesPage.hint')}
      </p>

      {/* Filter Tabs */}
      <div className="flex items-center gap-3 mb-4 mt-8">
        <button
          onClick={() => setFilter('all')}
          className={cn(
            'filter-tab',
            filter === 'all' && 'active'
          )}
        >
          {t('likesPage.all')}
          <span className="ml-1">{stats.total}</span>
        </button>
        <button
          onClick={() => setFilter('favorite')}
          className={cn(
            'filter-tab',
            filter === 'favorite' && 'active'
          )}
        >
          {t('likesPage.shortlist')}
          <span className="ml-1">{stats.favorites}</span>
        </button>
      </div>

      {/* Likes List */}
      <section>
        {likes.length === 0 ? (
          <div className="glass-effect rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-white/60" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {filter === 'all' ? t('likesPage.empty') : t('likesPage.emptyFavorites')}
            </h3>
            <p className="text-white/70">
              {t('likesPage.emptyHint')}
            </p>
          </div>
        ) : (
          likes.map((like) => (
            <div key={like.swipe_id} className="name-list-card">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-semibold text-lg">{like.name}</span>
                  <GenderIcon gender={like.gender} />
                </div>
                {like.meaning && (
                  <div className="text-white/80 italic text-sm">{like.meaning}</div>
                )}
              </div>
              <div className="flex items-center gap-4">
                {confirmDeleteId === like.swipe_id ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRemove(like.swipe_id)}
                      disabled={removingId === like.swipe_id}
                      className="text-red-400 text-sm font-medium whitespace-nowrap"
                    >
                      {removingId === like.swipe_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>{like.name} {t('likesPage.remove')}</>
                      )}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="list-action-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => toggleFavorite(like.swipe_id, like.swipe_type)}
                      className={cn(
                        'list-action-btn favorite',
                        like.swipe_type === 'favorite' && 'text-pp-orange'
                      )}
                    >
                      <Star className={cn('w-5 h-5', like.swipe_type === 'favorite' && 'fill-current')} />
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(like.swipe_id)}
                      className="list-action-btn delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </section>

    </>
  );
}
